/**
 * Applies the published `white_label` policy to the operator console's own
 * chrome.
 *
 * The Appearance Studio has always *stored* this policy; nothing consumed it,
 * so every switch in its "White-label dashboard" card was inert. This hook is
 * the consumer.
 *
 * Two contracts carry over from the design handoff's `operator-tenant.js`:
 *
 *  - **Published only.** The values here come from `useAppearanceStudio()`,
 *    which only broadcasts on a successful save. A draft the operator is still
 *    typing must never repaint the live console -- see the save() comment in
 *    appearance-studio-context.tsx.
 *  - **Attribution is conditional on concealment.** "Powered by" appears only
 *    when the product name has actually been replaced by a tenant name. Its
 *    job is to restore attribution that white-labelling removed, so with
 *    nothing removed there is nothing to restore.
 *  - **Concealment is the only condition.** `white_label.show_powered_by` no
 *    longer takes part: the owner's 2026-09-15 decision
 *    (docs/rebrand/satatam-decisions.md §12) makes the attribution
 *    non-suppressible, so a tenant who published `false` before that decision
 *    must not keep a hidden attribution the console can no longer restore.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";

import { useAppearance } from "./appearance-context.tsx";
import { useAppearanceStudio } from "./appearance-studio-context.tsx";
import { fontStack } from "./fontStacks.ts";

const PRODUCT_NAME = "Satatam";
/** The console's shipped accent. A tenant carrying this one has not chosen a
 *  colour, they are simply on the default. */
const PRODUCT_ACCENT = "#3FCF8E";

/** The two grounds the console paints on -- `--bg` in each theme. */
const DARK_GROUND = "#04120C";
const LIGHT_GROUND = "#F4F7F3";
export const DEFAULT_CONSOLE_LIGHT_BACKGROUND = LIGHT_GROUND;
export const DEFAULT_CONSOLE_LIGHT_FOREGROUND = "#07140C";
export const DEFAULT_CONSOLE_BACKGROUND = DARK_GROUND;
export const DEFAULT_CONSOLE_FOREGROUND = "#F4FBF7";
export type ConsoleThemeMode = "default" | "custom";

/** Curated operator surfaces. The widget's COLOR_PRESETS stay independent:
 * these values change the authenticated dashboard only. Every custom pair is
 * checked by the same 4.5:1 gate as a hand-entered colour. */
export const CONSOLE_THEME_PRESETS = {
  default: {
    label: "Satatam",
    tag: "Product",
    background: DEFAULT_CONSOLE_BACKGROUND,
    foreground: DEFAULT_CONSOLE_FOREGROUND,
    lightBackground: DEFAULT_CONSOLE_LIGHT_BACKGROUND,
    lightForeground: DEFAULT_CONSOLE_LIGHT_FOREGROUND,
  },
  obsidian: {
    label: "Obsidian",
    tag: "Black",
    background: "#080A0D",
    foreground: "#F3F6F8",
    lightBackground: "#F0F2F5",
    lightForeground: "#14191F",
  },
  graphite: {
    label: "Graphite",
    tag: "Neutral",
    background: "#17191D",
    foreground: "#F2F4F7",
    lightBackground: "#EEEFF1",
    lightForeground: "#1A1D22",
  },
  slate: {
    label: "Slate",
    tag: "Cool",
    background: "#17212D",
    foreground: "#F2F6FA",
    lightBackground: "#EDF2F7",
    lightForeground: "#182433",
  },
  plum: {
    label: "Plum",
    tag: "Colour",
    background: "#241A2F",
    foreground: "#FAF5FF",
    lightBackground: "#F5F0FA",
    lightForeground: "#2A2034",
  },
  sand: {
    label: "Sand",
    tag: "Light",
    background: "#F3EEE6",
    foreground: "#1A211D",
    lightBackground: "#FAF7F1",
    lightForeground: "#252921",
  },
} as const;
/** How far the optional page tint moves each backdrop stop toward the accent,
 *  per theme. Mirrors tokens.css's `--bg-grad-*` mixes exactly. */
const SURFACE_TINT_DARK = 0.06;
const SURFACE_TINT_LIGHT = 0.04;

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const RASTER_DATA_URL = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

const ACCENT_TOKENS = [
  "--mint", "--mint-soft", "--mint-deep", "--mint-text",
  "--mint-glow", "--mint-glow-2", "--mint-rim",
];

// The three properties tokens.css derives the hairlines and the backdrop from.
// Document-level and gated; a separate mechanism from the `.operator-brand`-scoped
// `--tenant-accent` / `--tenant-on-accent` that OperatorBrand.tsx sets for the
// sidebar mark alone, behind this same `consoleAccentIsLegible` console gate.
const TENANT_TOKENS = ["--console-accent", "--console-accent-deep", "--console-surface-tint", "--console-light-accent-text"];

/** Every custom property the console accent owns. A painter clears all of
 *  them before writing any, so switching tenants -- or handing a draft back
 *  to the published value -- can never leave half of one tenant's palette
 *  behind under another's. */
export const CONSOLE_ACCENT_TOKENS: readonly string[] = [...ACCENT_TOKENS, ...TENANT_TOKENS];

/** Properties owned by the whole-dashboard surface painter. Keeping these
 * separate from the accent list means a draft accent cannot accidentally
 * erase a published background, and a tenant switch clears both completely. */
export const CONSOLE_THEME_TOKENS: readonly string[] = [
  "--console-background", "--console-foreground", "--console-accent-text",
  "--console-light-background", "--console-light-foreground",
];

export interface ConsoleThemePaint {
  verdict: "default" | "custom" | "rejected";
  tokens: Readonly<Record<string, string>>;
}

export interface ConsoleAccentPaint {
  /** What `data-white-label-accent` reports: the product's own accent is not
   *  a choice ("off"), a legible tenant accent is applied ("on"), and an
   *  illegible one is refused ("rejected"). */
  verdict: "off" | "on" | "rejected";
  /** The properties to set. Everything in `CONSOLE_ACCENT_TOKENS` that is
   *  absent here is REMOVED, which is what makes this a whole-state paint
   *  rather than a patch. */
  tokens: Readonly<Record<string, string>>;
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  return 0.2126 * channelLuminance(parseInt(hex.slice(1, 3), 16))
    + 0.7152 * channelLuminance(parseInt(hex.slice(3, 5), 16))
    + 0.0722 * channelLuminance(parseInt(hex.slice(5, 7), 16));
}

function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** A custom console surface is also a text surface. Keep this gate at AA so
 * the dashboard never accepts a colour that makes its primary copy unreadable. */
export function consoleThemeIsLegible(background: string, foreground: string): boolean {
  return HEX_COLOR.test(background)
    && HEX_COLOR.test(foreground)
    && contrastRatio(background, foreground) >= 4.5;
}

/** Pick a readable default text colour for a hand-entered background. The
 * picker writes this pair together, while the server still validates both
 * values so API callers cannot bypass the WCAG gate. */
export function consoleForegroundFor(background: string): string {
  if (!HEX_COLOR.test(background)) return DEFAULT_CONSOLE_FOREGROUND;
  const light = DEFAULT_CONSOLE_FOREGROUND;
  const dark = "#07140C";
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}

/** `color-mix(in srgb, <top> <pct>%, <bottom>)`, computed here so the gates
 *  below test exactly the colour the CSS will paint. */
function mixHex(top: string, bottom: string, fraction: number): string {
  const channel = (i: number) => Math.round(parseInt(top.slice(i, i + 2), 16) * fraction
    + parseInt(bottom.slice(i, i + 2), 16) * (1 - fraction)).toString(16).padStart(2, "0");
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

function mixWithBlack(hex: string, fraction: number): string {
  return mixHex(hex, "#000000", fraction);
}

/** Preserve the selected hue while giving small accent text AA contrast on
 * each independently selected surface. */
function accentTextFor(accent: string, background: string): string {
  if (contrastRatio(accent, background) >= 4.5) return accent;
  const target = relativeLuminance(background) > 0.18 ? "#000000" : "#FFFFFF";
  for (const fraction of [0.75, 0.5, 0.25, 0]) {
    const candidate = mixHex(accent, target, fraction);
    if (contrastRatio(candidate, background) >= 4.5) return candidate;
  }
  return target;
}

/** How --mint-deep is derived from --mint. Approximates the product's own
 *  pair (#3FCF8E -> #146E40) closely enough to land at the same contrast. */
const DEEP_MIX = 0.5;

/**
 * Whether a tenant accent can carry the console's accent role.
 *
 * The console does not use one value on both grounds: `--mint` paints on the
 * dark ground, and the light theme reaches for `--mint-deep` wherever the
 * accent has to be legible as text. So the gate tests each variant against
 * the ground it actually appears on -- holding a tenant accent to the same
 * standard the product's own mint meets, rather than to a stricter one that
 * mint itself would fail (mint is 1.85:1 on the light ground).
 *
 * A rejected accent leaves the console on the product's mint, which is
 * legible by construction. Accepting an unreadable one would leave the
 * operator unable to see their own navigation, and no branding setting is
 * worth that.
 */
export function consoleAccentIsLegible(hex: string): boolean {
  return HEX_COLOR.test(hex)
    && contrastRatio(hex, DARK_GROUND) >= 3
    && contrastRatio(mixWithBlack(hex, DEEP_MIX), LIGHT_GROUND) >= 3;
}

/** Accent gate for a custom surface. The normal two-ground gate remains the
 * default contract; a custom mode paints one ground in both theme toggles, so
 * its accent only needs to clear that actual ground. */
export function consoleAccentIsLegibleForTheme(hex: string, background: string): boolean {
  return HEX_COLOR.test(hex)
    && HEX_COLOR.test(background)
    && contrastRatio(hex, background) >= 3;
}

/** The two ratios the gate measures, for the editor's readout. */
export function consoleAccentRatios(hex: string): { dark: number; light: number } {
  if (!HEX_COLOR.test(hex)) return { dark: 0, light: 0 };
  return {
    dark: contrastRatio(hex, DARK_GROUND),
    light: contrastRatio(mixWithBlack(hex, DEEP_MIX), LIGHT_GROUND),
  };
}

/** Whether the accent survives washing its own ground: the tint moves each
 *  backdrop stop toward the accent, so it can only lower contrast. Grounds
 *  mixed as tokens.css mixes them. */
export function consoleSurfaceTintIsLegible(hex: string): boolean {
  return consoleAccentIsLegible(hex)
    && contrastRatio(hex, mixHex(hex, DARK_GROUND, SURFACE_TINT_DARK)) >= 3
    && contrastRatio(
      mixWithBlack(hex, DEEP_MIX),
      mixHex(hex, LIGHT_GROUND, SURFACE_TINT_LIGHT),
    ) >= 3;
}

/**
 * The console's whole accent state, as data.
 *
 * Pure on purpose. Two surfaces need this answer -- the published chrome and
 * the Appearance editor's live draft preview -- and the one thing that must
 * never differ between them is WHICH colours a given accent produces. A
 * second implementation in the editor would be a second opinion, and the
 * first sign of a disagreement would be an operator publishing a console
 * that does not look like the preview they approved.
 *
 * Every token is emitted only after `accent` has cleared both the hex shape
 * and the legibility gate. A set-but-invalid custom property is worse than an
 * unset one: `color-mix()` reading it becomes invalid at computed-value time
 * and the `var()` fallback in tokens.css does not rescue it, so every
 * hairline in the console would paint `currentColor`.
 *
 * The page wash is its own decision, not a consequence of choosing a custom
 * accent: a tenant who opts in while still on the product's own mint gets a
 * mint-washed backdrop, which is the feature. `verdict` therefore keeps
 * describing the ACCENT alone and stays "off" for that tenant.
 */
export function consoleAccentPaint(
  accent: string,
  surfaceTint: boolean,
  theme?: { mode: ConsoleThemeMode; background: string; foreground: string; lightBackground?: string },
): ConsoleAccentPaint {
  const validAccent = HEX_COLOR.test(accent);
  const wantsAccent = validAccent && accent.toUpperCase() !== PRODUCT_ACCENT;
  const customTheme = theme?.mode === "custom"
    && consoleThemeIsLegible(theme.background, theme.foreground);
  const legible = wantsAccent && (customTheme
    ? consoleAccentIsLegibleForTheme(accent, theme!.background)
    : consoleAccentIsLegible(accent));
  const tokens: Record<string, string> = {};
  if (legible) {
    const accentText = customTheme ? accentTextFor(accent, theme!.background) : "";
    tokens["--mint"] = accent;
    tokens["--mint-soft"] = `color-mix(in srgb, ${accent} 72%, white)`;
    tokens["--mint-deep"] = customTheme
      ? accentText
      : `color-mix(in srgb, ${accent} ${DEEP_MIX * 100}%, black)`;
    if (customTheme) tokens["--mint-text"] = accentText;
    if (customTheme) tokens["--console-light-accent-text"] = accentTextFor(
      accent, theme!.lightBackground ?? DEFAULT_CONSOLE_LIGHT_BACKGROUND,
    );
    tokens["--mint-glow"] = `color-mix(in srgb, ${accent} 42%, transparent)`;
    tokens["--mint-glow-2"] = `color-mix(in srgb, ${accent} 16%, transparent)`;
    tokens["--mint-rim"] = `color-mix(in srgb, ${accent} 55%, transparent)`;
    tokens["--console-accent"] = accent;
    tokens["--console-accent-deep"] = mixWithBlack(accent, DEEP_MIX);
  }
  if (!customTheme && surfaceTint && validAccent && consoleSurfaceTintIsLegible(accent)) {
    tokens["--console-surface-tint"] = accent;
  }
  return { verdict: !wantsAccent ? "off" : legible ? "on" : "rejected", tokens };
}

/** Resolve one whole-dashboard surface state. Invalid custom input paints no
 * inline theme token, allowing CSS to stay on the product defaults while the
 * editor reports the precise field errors. */
export function consoleThemePaint(
  mode: ConsoleThemeMode,
  background: string,
  foreground: string,
  lightBackground: string = DEFAULT_CONSOLE_LIGHT_BACKGROUND,
  lightForeground: string = DEFAULT_CONSOLE_LIGHT_FOREGROUND,
): ConsoleThemePaint {
  if (mode !== "custom") return { verdict: "default", tokens: {} };
  if (!consoleThemeIsLegible(background, foreground)
    || !consoleThemeIsLegible(lightBackground, lightForeground)) {
    return { verdict: "rejected", tokens: {} };
  }
  return {
    verdict: "custom",
    tokens: {
      "--console-background": background,
      "--console-foreground": foreground,
      "--console-accent-text": foreground,
      "--console-light-background": lightBackground,
      "--console-light-foreground": lightForeground,
    },
  };
}

export function paintConsoleTheme(root: HTMLElement, paint: ConsoleThemePaint): void {
  for (const token of CONSOLE_THEME_TOKENS) root.style.removeProperty(token);
  for (const [token, value] of Object.entries(paint.tokens)) {
    root.style.setProperty(token, value);
  }
  root.dataset.consoleTheme = paint.verdict;
}

/** Apply one paint to a root element, clearing everything it does not set.
 *
 *  The ONLY writer of these properties. Two effects call it -- the published
 *  chrome and the Appearance editor's draft layer -- and because each call
 *  clears the full token set first, the later of the two always wins
 *  completely rather than leaving a mixture of both. */
export function paintConsoleAccent(root: HTMLElement, paint: ConsoleAccentPaint): void {
  for (const token of CONSOLE_ACCENT_TOKENS) root.style.removeProperty(token);
  for (const [token, value] of Object.entries(paint.tokens)) {
    root.style.setProperty(token, value);
  }
  root.dataset.whiteLabelAccent = paint.verdict;
}

export interface WhiteLabelChrome {
  /** The tenant's own name, or "" when the console is not white-labelled. */
  companyName: string;
  /** Whether the product's name is concealed throughout the chrome. */
  replaceProductName: boolean;
  /** Second line under the sidebar brand. */
  workspaceLabel: string;
  /** Prefer the tenant logo over the product eye / tenant initials. */
  useLogoAsMark: boolean;
  /** Render the attribution line in the sidebar footer. */
  showPoweredBy: boolean;
  /** What to call the product in running chrome -- tab titles, report
   *  headers, accessible names. The tenant name once they have concealed the
   *  product, the product name otherwise. Anything that would otherwise
   *  hard-code "Satatam" in the console reads this. */
  productLabel: string;
}

export function useWhiteLabelChrome(): WhiteLabelChrome {
  const { operatorBranding } = useAppearance();
  const { whiteLabel, widgetAppearanceExtras } = useAppearanceStudio();

  const rawName = typeof operatorBranding.display_name === "string"
    ? operatorBranding.display_name.trim()
    : "";
  // A tenant still carrying the product's own name has not white-labelled --
  // whether by default, by clearing the field, or by typing it back in.
  const companyName = rawName && rawName !== PRODUCT_NAME ? rawName : "";
  const branded = companyName !== "";
  const accent = typeof operatorBranding.accent === "string" ? operatorBranding.accent : "";
  const logo = typeof operatorBranding.logo_data_url === "string"
    && operatorBranding.logo_data_url.length <= 87_500
    && RASTER_DATA_URL.test(operatorBranding.logo_data_url)
    ? operatorBranding.logo_data_url
    : null;

  // "mark" swaps the sidebar logo only; "full" also applies the tenant
  // typography. Colour is no longer one of the levels -- see below.
  const retype = branded && whiteLabel.apply === "full";

  const surfaceTint = operatorBranding.console_surface_tint === true;
  const consoleThemeMode: ConsoleThemeMode = operatorBranding.console_theme_mode === "custom"
    ? "custom"
    : "default";
  const consoleBackground = typeof operatorBranding.console_background === "string"
    ? operatorBranding.console_background
    : DEFAULT_CONSOLE_BACKGROUND;
  const consoleForeground = typeof operatorBranding.console_foreground === "string"
    ? operatorBranding.console_foreground
    : DEFAULT_CONSOLE_FOREGROUND;
  const consoleLightBackground = typeof operatorBranding.console_light_background === "string"
    ? operatorBranding.console_light_background
    : DEFAULT_CONSOLE_LIGHT_BACKGROUND;
  const consoleLightForeground = typeof operatorBranding.console_light_foreground === "string"
    ? operatorBranding.console_light_foreground
    : DEFAULT_CONSOLE_LIGHT_FOREGROUND;

  // Console accent. Derived rather than assigned one-for-one: --mint has five
  // relatives (soft/deep/glow/glow-2/rim) and leaving those on the product hue
  // would put a mint halo around a tenant-coloured control.
  //
  // Every token below is written only after `accent` has cleared both the hex
  // shape and the legibility gate. A set-but-invalid custom property is worse
  // than an unset one: `color-mix()` reading it becomes invalid at
  // computed-value time and the `var()` fallback in tokens.css does not rescue
  // it, so every hairline in the console would paint `currentColor`.
  //
  // The effect cleans up after itself so that signing out, or switching to a
  // tenant who sets no accent, starts from a bare root rather than inheriting
  // the last tenant's inline properties.
  useEffect(() => {
    const root = document.documentElement;
    const theme = consoleThemePaint(consoleThemeMode, consoleBackground, consoleForeground, consoleLightBackground, consoleLightForeground);
    paintConsoleTheme(root, theme);
    paintConsoleAccent(
      root,
      consoleAccentPaint(
        accent,
        surfaceTint,
        theme.verdict === "custom"
          ? { mode: "custom", background: consoleBackground, foreground: consoleForeground, lightBackground: consoleLightBackground }
          : { mode: "default", background: DEFAULT_CONSOLE_BACKGROUND, foreground: DEFAULT_CONSOLE_FOREGROUND },
      ),
    );
    /* "off" rather than removing the attribute on the way out: `off` is the
       documented third value and the signed-out console is exactly that -- a
       console with no tenant accent -- while an absent attribute is a console
       that has not reported yet. Unmount is the only moment the attribute has
       nothing to describe. */
    return () => {
      paintConsoleAccent(root, { verdict: "off", tokens: {} });
      paintConsoleTheme(root, { verdict: "default", tokens: {} });
    };
  }, [
    accent, surfaceTint, consoleThemeMode, consoleBackground, consoleForeground,
    consoleLightBackground, consoleLightForeground,
  ]);

  // Typography, from the family the tenant already picked for their widget --
  // one setting, both surfaces. The mono face is deliberately left alone: it
  // is a data face, and IDs, ratios and timestamps keep their tabular
  // alignment whatever the tenant chose to read prose in.
  const tenantFont = widgetAppearanceExtras.typography.font;
  useEffect(() => {
    const root = document.documentElement;
    if (!retype) {
      root.style.removeProperty("--display");
      root.style.removeProperty("--sans");
      return;
    }
    const stack = fontStack(tenantFont);
    root.style.setProperty("--display", stack);
    root.style.setProperty("--sans", stack);
  }, [retype, tenantFont]);

  // Browser tab. Restores the original title on unmount so signing out does
  // not leave the tab wearing a tenant name.
  useEffect(() => {
    if (!branded || !whiteLabel.rename_browser_tab) return;
    const original = document.title;
    document.title = original.includes(PRODUCT_NAME)
      ? original.replace(PRODUCT_NAME, companyName)
      : `${companyName} · Operator`;
    return () => {
      document.title = original;
    };
  }, [branded, whiteLabel.rename_browser_tab, companyName]);

  // Favicon. Points the existing link at the logo data URL, and restores it on
  // unmount for the same reason as the title.
  useEffect(() => {
    if (!branded || !whiteLabel.use_logo_as_favicon || !logo) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) return;
    const originalHref = link.getAttribute("href");
    const originalType = link.getAttribute("type");
    link.setAttribute("href", logo);
    link.setAttribute("type", logo.slice(5, logo.indexOf(";")));
    return () => {
      if (originalHref === null) link.removeAttribute("href");
      else link.setAttribute("href", originalHref);
      if (originalType === null) link.removeAttribute("type");
      else link.setAttribute("type", originalType);
    };
  }, [branded, whiteLabel.use_logo_as_favicon, logo]);

  return useMemo<WhiteLabelChrome>(() => ({
    companyName,
    replaceProductName: branded && whiteLabel.replace_product_name,
    workspaceLabel: whiteLabel.workspace_label.trim() || companyName,
    useLogoAsMark: branded && whiteLabel.use_logo_as_mark,
    showPoweredBy: branded && whiteLabel.replace_product_name,
    productLabel:
      branded && whiteLabel.replace_product_name ? companyName : PRODUCT_NAME,
  }), [
    companyName, branded,
    whiteLabel.replace_product_name, whiteLabel.workspace_label,
    whiteLabel.use_logo_as_mark,
  ]);
}

/* The hook above is not re-entrant: it retints the console, renames the tab
   and repoints the favicon, so a second caller would run all of that a second
   time and fight the first over cleanup. The shell calls it once and
   publishes the result here; every other surface reads.
   Outside a provider the value is the un-white-labelled console -- which is
   what the auth pages and every test harness that mounts a page bare want. */
const UNBRANDED_CHROME: WhiteLabelChrome = {
  companyName: "",
  replaceProductName: false,
  workspaceLabel: "",
  useLogoAsMark: false,
  showPoweredBy: false,
  productLabel: PRODUCT_NAME,
};

const WhiteLabelChromeContext = createContext<WhiteLabelChrome>(UNBRANDED_CHROME);

export function WhiteLabelChromeProvider(
  { value, children }: { value: WhiteLabelChrome; children: ReactNode },
) {
  return (
    <WhiteLabelChromeContext.Provider value={value}>
      {children}
    </WhiteLabelChromeContext.Provider>
  );
}

/** The published chrome, for a surface that needs to name the product without
 *  owning the policy: tab titles, report headers, accessible names. */
export function usePublishedChrome(): WhiteLabelChrome {
  return useContext(WhiteLabelChromeContext);
}
